import "./Editor.css";

import { Color } from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import Link from "@tiptap/extension-link";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

import { Box } from "@mui/material";
import MenuBar from "./MenuBar";
import { FontSize } from "./extensions/fontSize";
import { IMenuBarProps } from "./models";
import { useEffect } from "react";
import TextAlign from "@tiptap/extension-text-align";
import CustomImage from "./extensions/customImage";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { normalizeTablesHtml } from "../../../utils/normalizeTablesHtml";

interface IEditorComponent {
  value?: string;
  error?: boolean;
  readOnly?: boolean;
  maxHeight?: string;
  menuBarProps?: IMenuBarProps;
  onlyCode?: boolean;
  onChange?: (value: string) => any;
  variables?: { name: string; code: string }[];
}

const MAX_TABLE_WIDTH = 595;

const EditorComponent: React.FC<IEditorComponent> = ({
  value = "",
  error = false,
  readOnly = false,
  maxHeight = "600px",
  variables = [],
  menuBarProps = {
    fontSize: true,
    markFormatting: true,
    selectColor: true,
    textFormatting: true,
    linkFormatting: true,
    textAlign: true,
    addImage: true,
    tableFormatting: true,
  },
  onlyCode = false,
  onChange,
}) => {
  const editor = useEditor({
    extensions: [
      Color,
      TextStyle,
      FontSize.configure({
        initialFontSize: "12",
      }),
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        codeBlock: onlyCode
          ? {
              exitOnArrowDown: false,
              exitOnTripleEnter: false,
              languageClassPrefix: "language-",
            }
          : undefined,
      }),
      CustomImage.configure({
        inline: false,
        allowBase64: true,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph", "image"],
      }),
      Link.configure({
        openOnClick: true,
        //autolink: true,
        // linkOnPaste: true,
        defaultProtocol: "https",
        protocols: ["http", "https"],
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value ? value : "",
    onUpdate: ({ editor }) => {
      const rawHtml = editor.getHTML();
      const normalizedHtml = normalizeTablesHtml(rawHtml, MAX_TABLE_WIDTH);

      if (onChange) {
        onChange(normalizedHtml);
      }
    },
    editable: !readOnly,
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  useEffect(() => {
    if (onlyCode) {
      if (editor && (!value || value.length === 0)) {
        editor.commands.setContent("<pre><code></code></pre>");
      }
      return;
    }
    if (editor && (!value || value.length === 0)) {
      editor.commands.setContent("<p></p>");
    }
  }, [onlyCode, value, editor]);

  useEffect(() => {
    if (
      (onlyCode && value.length === 0 && editor) ||
      (onlyCode && editor && value === "<p></p>")
    ) {
      editor.commands.setCodeBlock();
    }
  }, [editor, value, onlyCode]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      if (editor.getHTML() !== value) {
        editor.commands.setContent(value);
      }
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;

    const MAX_WIDTH = 595;

    const getPx = (value?: string | null) => {
      if (!value) return 0;
      const n = parseFloat(value.replace("px", "").trim());
      return Number.isFinite(n) ? n : 0;
    };

    const enforceTableWidth = () => {
      const tables = editor.view.dom.querySelectorAll("table");

      tables.forEach((tableEl) => {
        const table = tableEl as HTMLElement;
        const cols = Array.from(table.querySelectorAll("col")) as HTMLElement[];

        if (!cols.length) return;

        let widths = cols.map((col) => getPx(col.style.width));

        if (widths.some((w) => !w)) {
          const firstRowCells = Array.from(
            table.querySelectorAll("tr:first-child th, tr:first-child td"),
          ) as HTMLElement[];

          if (firstRowCells.length === cols.length) {
            widths = firstRowCells.map(
              (cell) => cell.getBoundingClientRect().width,
            );
          }
        }

        const total = widths.reduce((acc, w) => acc + w, 0);

        if (!total || total <= MAX_WIDTH) {
          table.style.maxWidth = `${MAX_WIDTH}px`;
          if (getPx(table.style.minWidth) > MAX_WIDTH) {
            table.style.minWidth = `${MAX_WIDTH}px`;
          }
          return;
        }

        const scale = MAX_WIDTH / total;

        const newWidths = widths.map((w) =>
          Math.max(24, Math.floor(w * scale)),
        );
        let newTotal = newWidths.reduce((acc, w) => acc + w, 0);

        if (newTotal !== MAX_WIDTH && newWidths.length > 0) {
          newWidths[newWidths.length - 1] += MAX_WIDTH - newTotal;
          newTotal = newWidths.reduce((acc, w) => acc + w, 0);
        }

        cols.forEach((col, i) => {
          col.style.width = `${newWidths[i]}px`;
        });

        table.style.width = `${MAX_WIDTH}px`;
        table.style.minWidth = `${MAX_WIDTH}px`;
        table.style.maxWidth = `${MAX_WIDTH}px`;
      });
    };

    const observer = new MutationObserver(() => {
      requestAnimationFrame(enforceTableWidth);
    });

    observer.observe(editor.view.dom, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    const onMouseUp = () => requestAnimationFrame(enforceTableWidth);
    document.addEventListener("mouseup", onMouseUp);

    requestAnimationFrame(enforceTableWidth);

    return () => {
      observer.disconnect();
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [editor]);
  return (
    <Box
      sx={(theme) => ({
        border: !onlyCode ? "1px solid" : undefined,
        borderRadius: theme.shape.borderRadius - 7,
        borderColor: error ? theme.palette.error.main : theme.palette.divider,
        backgroundColor: !onlyCode ? undefined : "#0D0D0D",
        overflow: "auto",
        maxHeight: maxHeight,
        position: "relative",
      })}
    >
      {!readOnly && !onlyCode && (
        <Box
          padding={2}
          paddingBottom={0}
          sx={(theme) => ({
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: theme.palette.background.default,
            paddingBottom: "10px",
            borderBottom: "1px solid ",
            borderBottomColor: theme.palette.divider,
          })}
        >
          <MenuBar
            editor={editor}
            menuBarProps={menuBarProps}
            variables={variables}
          />
        </Box>
      )}

      <Box padding={2}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default EditorComponent;
